/**
 * The permission picker, once (FS-007b).
 *
 * Roles and permission sets both grant permissions, so the admin page had two
 * copies of the same chip grid. Both rendered `p.split(".")[1]` — "view",
 * "send", "manage" — which is the code's language and, worse, flattens the
 * distinction the backend registry draws: it cannot tell the person handing
 * out access that `banking.manage` changes where money goes while
 * `inventory.view` lists machines.
 *
 * The registry now reaches the UI as `catalogue` on
 * `/api/v1/permissions/all-permissions`, carrying each permission's label in
 * the dealer's language, how much damage it can do, and what it implies. Two
 * copies of the rendering would mean two places deciding how a critical
 * permission looks, so there is one.
 *
 * Implications are shown rather than enforced. `banking.send` implies
 * `banking.view`; a chip grid that silently ticked the second when you ticked
 * the first would be guessing, and the resolver expands it anyway. The chip
 * says what comes with it instead, and the server refuses a revocation that
 * would have no effect.
 */

import type { PermissionDeclaration } from "./useCatalogue";

/** Marks the chips an admin should look twice at. */
const SENSITIVITY_MARK: Record<string, string> = {
  critical: "border-[var(--text-danger)]/40 text-[var(--text-danger)]",
  elevated: "border-[var(--border-strong)] text-[var(--text-secondary)]",
  routine: "border-[var(--border-default)] text-[var(--text-tertiary)]",
};

interface Props {
  groups: Record<string, string[]>;
  catalogue: Record<string, PermissionDeclaration>;
  selected: Set<string>;
  onToggle: (permission: string) => void;
  /** Ticking a whole group at once. Omitted where the group header is static. */
  onToggleGroup?: (permissions: string[]) => void;
}

function PermissionChipGrid({
  groups,
  catalogue,
  selected,
  onToggle,
  onToggleGroup,
}: Props) {
  return (
    <div className="space-y-3">
      {Object.entries(groups).map(([group, perms]) => {
        const allSelected = perms.every((p) => selected.has(p));
        return (
          <div key={group}>
            {onToggleGroup ? (
              <button
                type="button"
                onClick={() => onToggleGroup(perms)}
                className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={allSelected}
                  className="h-3 w-3 rounded border-[var(--border-default)]"
                />
                {group}
              </button>
            ) : (
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]">
                {group}
              </p>
            )}
            <div className="ml-5 flex flex-wrap gap-1.5">
              {perms.map((p) => {
                const declared = catalogue[p];
                // No catalogue entry means the backend and the frontend
                // disagree about what exists. Show the key rather than
                // nothing, so it is visible instead of silently missing.
                const label = declared?.label ?? p;
                const implies = declared?.implies ?? [];
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onToggle(p)}
                    title={
                      implies.length > 0
                        ? `${p} — also grants ${implies.join(", ")}`
                        : p
                    }
                    className={`rounded-[var(--radius-pill)] border px-2 py-1 text-[11px] transition-all ${
                      selected.has(p)
                        ? "border-[var(--color-interactive-primary)]/30 bg-[var(--color-interactive-primary)]/10 text-[var(--color-interactive-primary)]"
                        : (SENSITIVITY_MARK[
                            declared?.sensitivity ?? "routine"
                          ] ?? SENSITIVITY_MARK.routine) +
                          " hover:border-[var(--border-strong)]"
                    }`}
                  >
                    {label}
                    {declared?.sensitivity === "critical" && (
                      <span
                        className="ml-1 font-semibold"
                        aria-label="high-risk permission"
                      >
                        !
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PermissionChipGrid;
